// ---------------------------------------------------------------------------
// templateRefs — resource-agnostic detection of template references embedded in
// a resource's text fields. The platform reserves `{{ }}` for two forms:
//   • `{{$var:NAME}}`  — a declared-variable reference (bound at eval time).
//   • `{{$expr:...}}`  — a runtime query/data expression.
// A bare `{{NAME}}` is neither: it is invisible to binding and is not
// substituted at run time. Both agents and graders embed these in their spec
// text fields, so this module works over a generic list of named text fields
// (a `field` key + its current `text`) rather than any one form-state shape.
// ---------------------------------------------------------------------------

/** A named text field to scan (e.g. `{ field: 'systemPrompt', text: '…' }`). */
export interface NamedText<F extends string = string> {
  field: F;
  text: string;
}

/** A bare `{{...}}` token that is neither `$var:` nor `$expr:`. */
export interface BareTemplateToken<F extends string = string> {
  field: F;
  /** Full matched text, e.g. `{{ AGENT_MODEL }}` — used for exact replacement. */
  raw: string;
  /** Trimmed inner text, e.g. `AGENT_MODEL`. */
  inner: string;
}

const VAR_REFERENCE_PATTERN = /\{\{\s*\$var:\s*([^}]+?)\s*\}\}/g;
const BARE_TEMPLATE_PATTERN = /\{\{\s*(?!\$(?:var|expr):)([^}]+?)\s*\}\}/g;

// Variable keys follow UPPER_SNAKE_CASE (mirrors VARIABLE_NAME_PATTERN). A bare
// token is only offered a one-click `$var` conversion when its inner text would
// form a valid variable name, so we never generate an invalid `{{$var:…}}`.
const CONVERTIBLE_VAR_NAME = /^[A-Z][A-Z0-9_]*$/;

/** Distinct `$var` names referenced across the given text fields. */
export function collectReferencedVars<F extends string>(
  fields: readonly NamedText<F>[],
): string[] {
  const names = new Set<string>();
  for (const { text } of fields) {
    for (const match of text.matchAll(VAR_REFERENCE_PATTERN)) {
      const name = match[1]?.trim();
      if (name !== undefined && name.length > 0) names.add(name);
    }
  }
  return [...names];
}

/** Bare `{{...}}` tokens (not `$var:`/`$expr:`), de-duplicated per field. */
export function collectBareTemplateTokens<F extends string>(
  fields: readonly NamedText<F>[],
): BareTemplateToken<F>[] {
  const seen = new Set<string>();
  const tokens: BareTemplateToken<F>[] = [];
  for (const { field, text } of fields) {
    for (const match of text.matchAll(BARE_TEMPLATE_PATTERN)) {
      const inner = match[1]?.trim();
      if (inner === undefined || inner.length === 0) continue;
      const key = `${field}::${match[0]}`;
      if (seen.has(key)) continue;
      seen.add(key);
      tokens.push({ field, raw: match[0], inner });
    }
  }
  return tokens;
}

/** Rewrite every occurrence of a bare token in `text` as `{{$var:INNER}}`. */
export function replaceBareTokenWithVar<F extends string>(
  text: string,
  token: BareTemplateToken<F>,
): string {
  return text.split(token.raw).join(`{{$var:${token.inner}}}`);
}

/** Whether a bare token's inner text can become a valid `{{$var:NAME}}`. */
export function isConvertibleVarName(inner: string): boolean {
  return CONVERTIBLE_VAR_NAME.test(inner);
}
